import type { ReactNode } from "react";
import {
  Platform,
  ScrollView,
  StyleSheet,
  useWindowDimensions,
  View,
} from "react-native";

import {
  AppLink,
  BlueprintGrid,
  BrandMark,
  PageTitle,
  Screen,
  WebFooter,
} from "~/components/ui";
import { WebStoreBadges } from "~/components/composed/WebStoreBadges";
import {
  radii,
  shadow,
  spacing,
  useThemedStyles,
  type ThemeColors,
} from "~/styles/theme";
import { getSiteUrl } from "~/utils/runtime-config";

export function AuthScreen({
  children,
  title,
  subtitle,
}: {
  children: ReactNode;
  title: string;
  subtitle?: string;
}) {
  const stylesheet = useThemedStyles(makeStyles);
  const siteUrl = getSiteUrl();
  const { width } = useWindowDimensions();
  const compact = width < 480;

  return (
    <Screen style={stylesheet.screen}>
      <ScrollView contentContainerStyle={stylesheet.scroll}>
        <BlueprintGrid style={StyleSheet.absoluteFill} />
        <View
          style={[stylesheet.body, compact ? stylesheet.bodyCompact : null]}
        >
          <View style={stylesheet.content}>
            <View style={stylesheet.brand}>
              <BrandMark size="xl" />
            </View>
            <View
              style={[
                stylesheet.card,
                compact ? stylesheet.cardCompact : null,
                shadow("lg"),
              ]}
            >
              <PageTitle title={title} subtitle={subtitle} />
              {children}
            </View>
            {siteUrl && Platform.OS === "web" ? (
              <View style={stylesheet.backToWebsite}>
                <AppLink href={siteUrl as never} size="sm" align="center">
                  ← Back to website
                </AppLink>
              </View>
            ) : null}
          </View>
        </View>
        <WebStoreBadges />
        <WebFooter />
      </ScrollView>
    </Screen>
  );
}

const makeStyles = (colors: ThemeColors) =>
  StyleSheet.create({
    screen: { backgroundColor: colors.background },
    scroll: { flexGrow: 1 },
    body: {
      flexGrow: 1,
      width: "100%",
      alignItems: "center",
      justifyContent: "center",
      padding: spacing.xl,
    },
    bodyCompact: { padding: spacing.l },
    content: {
      width: "100%",
      maxWidth: 440,
      gap: spacing.l,
    },
    brand: {
      paddingHorizontal: spacing.xl,
      flexDirection: "row",
      justifyContent: "center",
    },
    cardCompact: { padding: spacing.l },
    backToWebsite: { alignItems: "center" },
    card: {
      width: "100%",
      padding: spacing.xl,
      borderWidth: 1,
      borderColor: colors.borderStrong,
      borderRadius: radii.card,
      backgroundColor: colors.elevated,
      gap: spacing.l,
    },
  });
